// 工艺流程（/process-flow）
// 酪蛋白产业链数字化页「工艺流程」的下级页面
// 由 2223 版本移植：2221 舞台 7680×2160（2223 为 3840×2160），
// 横向尺寸与字号 ×2；纵向在 2160 预算内重排（分支槽/面板内边距按实测收敛，避免溢出）
<template>
  <div class="pf-page">
    <div class="pf-viewport" ref="viewport">
      <div class="pf-scale-box" ref="box">
        <div class="pf-stage" ref="stage">
          <!-- 页头标题条 -->
          <header class="pf-head">
            <div class="pf-banner">
              <i class="pf-banner-dot"></i>
              <span class="pf-banner-text">{{ $C('processFlow','flowTitle','酪蛋白生产工艺流程') }}</span>
            </div>
            <div class="pf-banner-line"></div>
            <button class="pf-back" type="button" @click="goBack">
              <span class="pf-back-arrow">←</span> {{ fromChain ? $C('processFlow','backFromChain','返回产业链') : $C('processFlow','backDefault','返回') }}
            </button>
          </header>

          <!-- 工艺流程面板：16 列（主线 + 分支槽） -->
          <section class="pf-panel">
            <div class="pf-grid">
              <div class="pf-col" v-for="step in cols" :key="step.key">
                <!-- 主线节点 -->
                <div class="pf-node" :class="{ 'pf-node-store': step.key === 'store' }" @click="openStep(step)">
                  <div class="pf-podium">
                    <span class="pf-glow"></span>
                    <span class="pf-plate"></span>
                    <span class="pf-icon"><FlowIcon :name="step.icon" /></span>
                  </div>
                  <p class="pf-label">{{ step.name }}<i v-if="step.sub" class="pf-sub">{{ step.sub }}</i></p>
                </div>
                <!-- 主线箭头（列间） -->
                <svg v-if="step.key !== 'store'" class="pf-arrow" width="68" height="32" viewBox="0 0 34 16" fill="none">
                  <path d="M1 8h24" stroke="#4fd8ff" stroke-width="2.6" stroke-linecap="round" />
                  <path d="M24 1.5L32.5 8 24 14.5z" fill="#4fd8ff" />
                </svg>
                <!-- 成品检验 → 存储：合格标签 -->
                <span v-if="step.key === 'qc3'" class="pf-flag pf-flag-ok">{{ $C('processFlow','okFlag','合格') }}</span>
                <!-- 分支槽：不合格 ↓ -->
                <div class="pf-branch-slot">
                  <template v-if="step.reject">
                    <svg class="pf-arrow-v" width="32" height="100" viewBox="0 0 16 64" fill="none">
                      <path d="M8 2v50" stroke="#ffb35c" stroke-width="2.6" stroke-linecap="round" stroke-dasharray="7 6" />
                      <path d="M1.5 50L8 62l6.5-12z" fill="#ffb35c" />
                    </svg>
                    <span class="pf-flag pf-flag-no">{{ step.reject }}</span>
                  </template>
                  <div
                    class="pf-node pf-node-reject"
                    v-if="branchOf(step)"
                    @click="openStep(branchOf(step))"
                  >
                    <div class="pf-podium">
                      <span class="pf-glow pf-glow-no"></span>
                      <span class="pf-plate pf-plate-no"></span>
                      <span class="pf-icon pf-icon-no"><FlowIcon :name="branchOf(step).icon" /></span>
                    </div>
                    <p class="pf-label pf-label-no">{{ branchOf(step).name }}</p>
                  </div>
                </div>
              </div>
            </div>
            <p class="pf-tip">{{ tip }}</p>
          </section>

          <!-- 底部图例说明 -->
          <section class="pf-legend">
            <span class="pf-lg"><i class="pf-lg-main"></i>{{ $C('processFlow','legendMain','主线工序（16 道）') }}</span>
            <span class="pf-lg"><i class="pf-lg-reject"></i>{{ $C('processFlow','legendReject','不合格分支处理（3 项）') }}</span>
            <span class="pf-lg"><i class="pf-lg-ok"></i>{{ $C('processFlow','legendOk','合格放行') }}</span>
            <span class="pf-lg pf-lg-hint">{{ $C('processFlow','legendHint','点击任一工序，播放该工序的生产演示动态视频') }}</span>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FlowIcon from '@/components/ganzi/FlowIcon.vue';
import { PROCESS_STEPS } from '@/data/processFlow';

export default {
  name: 'processFlow',
  components: { FlowIcon },
  data() {
    return {
      scale: 1,
      tip: '',
    };
  },
  computed: {
    fromChain() {
      const f = this.$route.query && this.$route.query.from;
      return f === 'industry-chain';
    },
    // 工序节点数据：后台可整体编辑（名称/副标题/不合格标签等），未配置时用内置默认
    steps() {
      return this.$CList('processFlow', 'steps', PROCESS_STEPS);
    },
    cols() {
      // 主线 16 道为一列；每列携带自身的不合格分支节点
      return this.steps.filter(s => s.branch === '');
    },
  },
  created() {
    this.tip = this.$C('processFlow', 'tipText', '共 19 个工序节点 · 全程可追溯');
  },
  mounted() {
    this.$nextTick(() => this.calc());
    window.addEventListener('resize', this.calc);
  },
  activated() {
    this.$nextTick(() => this.calc());
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calc);
  },
  methods: {
    branchOf(step) {
      if (!step.reject) return null;
      return this.steps.find(s => s.branch === 'reject' && s.parent === step.key) || null;
    },
    openStep(step) {
      this.$router.push({ path: '/process-demo/' + encodeURIComponent(step.name), query: { from: 'process-flow' } });
    },
    goBack() {
      if (this.fromChain) this.$router.replace('/industry-chain');
      else if (window.history.length > 1) this.$router.back();
      else this.$router.replace('/industry-chain');
    },
    // 7680×2160 舞台等比"包含"缩放，整页一次看完不滚动
    calc() {
      const vp = this.$refs.viewport;
      const stage = this.$refs.stage;
      const box = this.$refs.box;
      if (!vp || !stage || !box) return;
      const s = Math.min(vp.clientWidth / 7680, vp.clientHeight / 2160);
      this.scale = s;
      stage.style.transform = `scale(${s})`;
      box.style.width = 7680 * s + 'px';
      box.style.height = 2160 * s + 'px';
    },
  },
};
</script>

<style lang="less" scoped>
.pf-page {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background:
    radial-gradient(1200px 600px at 50% 110%, rgba(20, 70, 150, 0.35), rgba(0, 0, 0, 0) 70%),
    radial-gradient(900px 500px at 50% -10%, rgba(15, 60, 130, 0.4), rgba(0, 0, 0, 0) 70%),
    linear-gradient(165deg, #030b1e 0%, #061634 55%, #030b1e 100%);
  overflow: hidden;
  font-family: 'Microsoft YaHei', 'PingFang SC', sans-serif;
}
.pf-viewport {
  position: absolute;
  inset: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pf-scale-box { position: relative; }
.pf-stage {
  width: 7680px;
  height: 2160px;
  transform-origin: top left;
  padding: 110px 140px 80px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 46px;
}

/* ---------- 页头标题条 ---------- */
.pf-head {
  flex: none;
  display: flex;
  align-items: center;
  gap: 70px;
}
.pf-banner {
  flex: none;
  display: flex;
  align-items: center;
  gap: 34px;
  padding: 40px 130px 40px 80px;
  background: linear-gradient(90deg, rgba(20, 90, 190, 0.9) 0%, rgba(30, 120, 220, 0.55) 70%, rgba(30, 120, 220, 0) 100%);
  clip-path: polygon(0 0, 100% 0, calc(100% - 80px) 100%, 0 100%);
  border-top: 5px solid rgba(120, 220, 255, 0.9);
}
.pf-banner-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #7de9ff;
  box-shadow: 0 0 28px rgba(120, 230, 255, 0.95);
}
.pf-banner-text {
  font-size: 88px;
  font-weight: 700;
  letter-spacing: 16px;
  color: #fff;
  text-shadow: 0 0 36px rgba(80, 200, 255, 0.8);
  white-space: nowrap;
}
.pf-banner-line {
  flex: 1;
  height: 5px;
  margin-top: 18px;
  background: linear-gradient(90deg, rgba(90, 190, 255, 0.8), rgba(90, 190, 255, 0.05));
}
.pf-back {
  flex: none;
  background: rgba(0, 229, 255, 0.12);
  border: 2px solid rgba(0, 229, 255, 0.55);
  color: #00E5FF;
  font-size: 56px;
  letter-spacing: 6px;
  padding: 26px 68px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { background: rgba(0, 229, 255, 0.25); box-shadow: 0 0 36px rgba(0, 229, 255, 0.5); }
}
.pf-back-arrow { margin-right: 12px; }

/* ---------- 流程面板 ---------- */
.pf-panel {
  flex: 1;
  min-height: 0;
  border: 4px solid rgba(70, 140, 230, 0.45);
  border-radius: 52px;
  background: linear-gradient(180deg, rgba(16, 48, 104, 0.42) 0%, rgba(8, 26, 62, 0.55) 100%);
  box-shadow: inset 0 0 120px rgba(30, 100, 200, 0.18);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 40px;
  padding: 50px 60px 40px;
}
.pf-grid {
  display: flex;
  align-items: flex-start;
  width: 100%;
}
.pf-col {
  position: relative;
  flex: 1;
  min-width: 0;
  /* 列内纵向排布：节点在上、分支槽在下；行向排布会把 480px 分支槽横向挤进
     约 454px 的列里，导致首节点出界、分支徽章压住相邻列标签 */
  display: flex;
  flex-direction: column;
  align-items: center;
}
.pf-node {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  user-select: none;
  transition: transform 0.2s ease;
  width: 100%;
  &:hover { transform: translateY(-16px); }
  &:hover .pf-icon { transform: translateX(-50%) scale(1.16); }
  &:hover .pf-label { color: #aef2ff; text-shadow: 0 0 32px rgba(90, 220, 255, 0.95); }
}
.pf-podium {
  position: relative;
  width: 380px;
  /* 纵向预算有限：台座高度收敛，保证下方不合格分支节点不溢出面板 */
  height: 300px;
}
.pf-glow {
  position: absolute;
  left: 50%;
  bottom: -20px;
  width: 380px;
  height: 160px;
  transform: translateX(-50%);
  background: radial-gradient(closest-side, rgba(40, 190, 255, 0.55), rgba(40, 190, 255, 0) 72%);
}
.pf-plate {
  position: absolute;
  left: 50%;
  bottom: 0;
  width: 324px;
  height: 108px;
  transform: translateX(-50%);
  border-radius: 50%;
  background:
    radial-gradient(ellipse at 50% 30%, rgba(120, 230, 255, 0.5) 0%, rgba(30, 110, 210, 0.35) 45%, rgba(10, 40, 100, 0.65) 100%);
  border: 4px solid rgba(140, 235, 255, 0.85);
  box-shadow:
    0 12px 48px rgba(40, 170, 255, 0.45),
    inset 0 4px 20px rgba(160, 240, 255, 0.5);
}
.pf-plate::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 204px;
  height: 64px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: 2px solid rgba(170, 240, 255, 0.55);
  background: radial-gradient(ellipse at 50% 40%, rgba(80, 190, 255, 0.5), rgba(20, 70, 150, 0.25) 80%);
}
.pf-icon {
  position: absolute;
  left: 50%;
  bottom: 48px;
  width: 184px;
  height: 184px;
  transform: translateX(-50%);
  transition: transform 0.2s ease;
  filter: drop-shadow(0 0 24px rgba(80, 220, 255, 0.9));
}
.pf-label {
  margin-top: 40px;
  max-width: 460px;
  font-size: 52px;
  font-weight: 600;
  letter-spacing: 4px;
  line-height: 76px;
  color: #eaf6ff;
  text-align: center;
  text-shadow: 0 0 20px rgba(70, 180, 255, 0.6);
  .pf-sub {
    font-style: normal;
    display: block;
    font-size: 44px;
    font-weight: 400;
    color: rgba(160, 210, 255, 0.9);
    letter-spacing: 2px;
    line-height: 60px;
  }
}
/* 列改纵向后箭头不再占流式宽度，绝对定位骑在列边界上 */
.pf-arrow { position: absolute; right: -34px; top: 110px; z-index: 2; }
.pf-flag {
  position: absolute;
  font-size: 44px;
  letter-spacing: 4px;
  padding: 4px 28px;
  border-radius: 999px;
  white-space: nowrap;
}
.pf-flag-ok {
  top: 192px;
  right: -36px;
  color: #4dffa0;
  border: 2px solid rgba(80, 255, 160, 0.7);
  background: rgba(20, 90, 60, 0.55);
}
/* 分支槽改为常规流布局：紧随所属工序下方，避免绝对定位撑出网格后被居中布局二次溢出 */
.pf-branch-slot {
  /* 纵向列布局下紧跟标签之后，小间距即可 */
  margin-top: 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.pf-arrow-v { margin-bottom: 8px; }
.pf-flag-no {
  /* 分支槽内的"虚弃处理"标签必须占流式空间，绝对定位会让下方分支节点上移压住它 */
  position: static;
  color: #ffc46b;
  border: 2px solid rgba(255, 190, 100, 0.75);
  background: rgba(110, 70, 20, 0.55);
  margin-bottom: 16px;
}
.pf-node-reject {
  &:hover { transform: translateY(-12px); }
  &:hover .pf-label-no { color: #ffd9a8; }
}
.pf-glow-no { background: radial-gradient(closest-side, rgba(255, 170, 80, 0.4), rgba(255, 170, 80, 0) 72%); }
.pf-plate-no {
  border-color: rgba(255, 195, 120, 0.8);
  background:
    radial-gradient(ellipse at 50% 30%, rgba(255, 200, 130, 0.4) 0%, rgba(160, 100, 40, 0.35) 45%, rgba(70, 40, 12, 0.6) 100%);
  box-shadow: 0 12px 44px rgba(255, 170, 80, 0.3), inset 0 4px 20px rgba(255, 220, 170, 0.4);
}
.pf-icon-no { filter: drop-shadow(0 0 24px rgba(255, 190, 110, 0.85)); }
.pf-icon-no .flow-icon { stroke: #ffd9a8; }
.pf-label-no {
  color: #ffe6c8;
  text-shadow: 0 0 20px rgba(255, 180, 100, 0.6);
  font-size: 52px;
}
.pf-tip {
  font-size: 52px;
  letter-spacing: 12px;
  color: rgba(160, 210, 255, 0.75);
}

/* ---------- 底部图例 ---------- */
.pf-legend {
  flex: none;
  height: 230px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 140px;
  border: 2px solid rgba(70, 140, 230, 0.35);
  border-radius: 40px;
  background: linear-gradient(180deg, rgba(14, 42, 92, 0.4), rgba(8, 24, 58, 0.5));
}
.pf-lg {
  display: flex;
  align-items: center;
  gap: 32px;
  font-size: 56px;
  letter-spacing: 6px;
  color: rgba(190, 220, 250, 0.9);
  white-space: nowrap;
}
.pf-lg-main {
  width: 88px; height: 24px; border-radius: 12px;
  background: linear-gradient(90deg, #2f9dff, #31FFFF);
}
.pf-lg-reject {
  width: 88px; height: 24px; border-radius: 12px;
  background: linear-gradient(90deg, #ffab52, #ffd9a8);
}
.pf-lg-ok {
  width: 48px; height: 48px; border-radius: 50%;
  background: radial-gradient(circle at 40% 35%, #7dffb0, #23c86a 70%);
}
.pf-lg-hint { color: rgba(140, 200, 255, 0.85); }
</style>
